import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { toast, ToastContainer } from 'react-toastify';

/**
 * Custom Toast Icon component that wraps the default icon in nested elements
 * with animation effects
 *
 * @param {Object} props - Component props
 * @returns {React.ReactElement} - Nested icon structure
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
const CustomToastIcon = ({ children }) => (
  <span>
    <span className='pulse position-absolute all-0 d-flex flex-center'>
      <span className='position-absolute all-0 d-flex flex-center'>
        {children}
      </span>
    </span>
  </span>
);

CustomToastIcon.propTypes = {
  children: PropTypes.node.isRequired
};

// Default configuration constants
const DEFAULT_TOAST_CONFIG = {
  icon: ({ type }) => {
    let iconElement = null;
    switch (type) {
      case 'info':
        iconElement = (
          <FontAwesomeIcon icon='info-circle' transform='grow-10' beat />
        );
        break;
      case 'success':
        iconElement = (
          <FontAwesomeIcon icon='check-circle' transform='grow-10' beat />
        );
        break;
      case 'warning':
        iconElement = (
          <FontAwesomeIcon
            icon='exclamation-triangle'
            transform='grow-10'
            beat
          />
        );
        break;
      case 'error':
        iconElement = (
          <FontAwesomeIcon icon='exclamation-circle' transform='grow-10' beat />
        );
        break;
      default:
        iconElement = (
          <FontAwesomeIcon icon='info-circle' transform='grow-10' beat />
        );
        break;
    }
    return <CustomToastIcon>{iconElement}</CustomToastIcon>;
  },
  autoClose: 3000,
  theme: 'colored',
  hideProgressBar: false,
  newestOnTop: true,
  closeOnClick: true,
  rtl: false,
  draggable: false,
  pauseOnHover: true,
  position: toast.POSITION.BOTTOM_CENTER,
  role: 'alert',
  limit: 5,
  closeButton: true,
  className: 'leargas-toast-container',
  style: { zIndex: 9999 }
};

/**
 * Configurable Toast Container component
 *
 * This component is a wrapper around the react-toastify ToastContainer.
 * It allows for easy customization of toast notifications
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.icon - Show icon in toast
 * @param {number|boolean} props.autoClose - Auto close duration in milliseconds or false to disable
 * @param {string} props.theme - Theme for the toast (light, dark, colored)
 * @param {boolean} props.hideProgressBar - Hide progress bar
 * @param {boolean} props.newestOnTop - Show newest toasts on top
 * @param {boolean} props.closeOnClick - Close toast on click
 * @param {boolean} props.rtl - Right to left layout
 * @param {number} props.maxToasts - Maximum number of toasts to show
 * @param {boolean} props.draggable - Allow dragging to dismiss
 * @param {boolean} props.pauseOnHover - Pause auto close on hover
 * @param {string} props.position - Position of the toast (top-left, top-center, etc.)
 * @param {string} props.className - Additional class for the container
 * @param {string} props.toastClassName - Additional class for the toast
 * @param {string} props.transition - Transition effect for toasts
 *
 * @returns {React.ReactNode} The rendered ToastContainer component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
const LSToastContainer = props => {
  return <ToastContainer {...DEFAULT_TOAST_CONFIG} {...props} />;
};

LSToastContainer.propTypes = {
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  className: PropTypes.string,
  closeButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  closeOnClick: PropTypes.bool,
  draggable: PropTypes.bool,
  hideProgressBar: PropTypes.bool,
  icon: PropTypes.bool,
  limit: PropTypes.number,
  maxToasts: PropTypes.number,
  newestOnTop: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  position: PropTypes.oneOf([
    toast.POSITION.TOP_LEFT,
    toast.POSITION.TOP_CENTER,
    toast.POSITION.TOP_RIGHT,
    toast.POSITION.BOTTOM_LEFT,
    toast.POSITION.BOTTOM_CENTER,
    toast.POSITION.BOTTOM_RIGHT
  ]),
  role: PropTypes.string,
  rtl: PropTypes.bool,
  theme: PropTypes.oneOf(['light', 'dark', 'colored']),
  toastClassName: PropTypes.string,
  transition: PropTypes.string
};

/**
 * Displays an error toast notification with the given message.
 * @param {string} message - The error message to be displayed.
 * @param {Object} [options={}] - Additional configuration options to override defaults.
 * @returns {*} The result of the toast.error function.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const showError = (message, options = {}) =>
  toast.error(message, options);

/**
 * Displays an info toast message with the default configuration.
 *
 * @param {string} message - The message to be displayed in the toast
 * @param {Object} [options={}] - Optional configuration to override default toast settings
 * @returns {React.ReactText} The toast ID
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const showInfo = (message, options = {}) => toast.info(message, options);

/**
 * Displays a success toast notification with the provided message.
 *
 * @param {string} message - The message to display in the toast notification.
 * @param {Object} [options={}] - Optional configuration to override the default toast settings.
 * @returns {*} The return value of the toast.success function.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const showSuccess = (message, options = {}) =>
  toast.success(message, options);

/**
 * Displays a warning toast message.
 *
 * @param {string} message - The message to display in the warning toast.
 * @param {Object} [options={}] - Optional configuration to override default toast settings.
 * @returns {React.ReactText} The toast notification identifier that can be used with dismiss.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const showWarning = (message, options = {}) =>
  toast.warning(message, options);

/**
 * Displays a toast notification with the provided message.
 *
 * @param {string} message - The message to display in the toast.
 * @param {Object} [options={}] - Optional configuration to override default toast settings.
 * @returns {React.ReactText} The toast ID.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const showToast = (message, options = {}) => toast(message, options);

// Optimize with React.memo to prevent unnecessary re-renders
export default React.memo(LSToastContainer);
